import React, { useEffect, useState, useCallback } from 'react';
import { Form, Formik, type FormikHelpers } from 'formik';
import { PageHeader, PageWrapper } from '../company-profile/profile.styled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import LogoUpload from './logoUpload';
import FaviconUpload from './faviconUpload';
import { WhiteLabelContainer, WhiteLabelSection } from './whiteLabel.styled';
import { WHITE_LABEL_LOGO_VALIDATION } from '../../../utils/validations/validations';
import Button from '../../../components/button';
import AlertPopup from '../../../components/alert-popup';
import InputText from '../../../components/input-text/inputText';
import {
  useAdvanceBoolean,
  useGetBrandingPackageDefault,
  usePostBrandingPackageLogo,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import usePermissionCheck from '../../../hooks/custom-hooks/usePermissionCheck';

// =============================================================================
// Types & Interfaces
// =============================================================================

interface ImagePreview {
  content: string | null;
  name: string | null;
}

interface BrandingFormValues {
  logo: File | null;
  logoPreview: ImagePreview;
  favicon: File | null;
  faviconPreview: ImagePreview;
  titlePrefix: string;
}

interface BrandingConfigAsset {
  type?: string;
  url?: string;
  is_external?: boolean;
}

interface BrandingConfig {
  logo?: BrandingConfigAsset;
  'fav-icon'?: BrandingConfigAsset;
  'page-title-prefix'?: string | null;
}

interface FormActions {
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void;
}

type DeleteType = 'logo' | 'favicon';

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_PREVIEW: ImagePreview = { content: null, name: null };

const FILE_CONFIG = {
  logo: {
    validTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    maxSize: 2 * 1024 * 1024, // 2MB
  },
  favicon: {
    validTypes: [
      'image/x-icon',
      'image/vnd.microsoft.icon',
      'image/png',
      'image/jpeg',
      'image/jpg',
    ],
    maxSize: 512 * 1024, // 512KB
  },
} as const;

// =============================================================================
// Utility Functions
// =============================================================================

const parseConfig = (configString?: string | null): BrandingConfig => {
  if (!configString) return {};
  try {
    return JSON.parse(configString);
  } catch {
    console.error('Error parsing branding config');
    return {};
  }
};

const buildImageUrl = (url: string, isExternal?: boolean): string => {
  return isExternal ? url : `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? ''}${url}`;
};

const isValidFile = (file: File, type: 'logo' | 'favicon'): boolean => {
  const config = FILE_CONFIG[type];
  return (
    (config.validTypes as readonly string[]).includes(file.type) && file.size <= config.maxSize
  );
};

const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// =============================================================================
// Component
// =============================================================================

const WhiteLabelConfiguration = (): JSX.Element => {
  // ---------------------------------------------------------------------------
  // Permission Check
  // ---------------------------------------------------------------------------
  const hasCreatePermission = usePermissionCheck({
    type: 'Create',
    feature: 'Customer',
    permission: 'BrandingPackage',
  });

  const hasEditPermission = usePermissionCheck({
    type: 'Edit',
    feature: 'Customer',
    permission: 'BrandingPackage',
  });

  // ---------------------------------------------------------------------------
  // API Hooks
  // ---------------------------------------------------------------------------
  const { handleRequest } = useWealthlaneIdentityApi();
  const { data: brandingPackage, isLoading } = useGetBrandingPackageDefault(handleRequest);
  const { mutate: uploadBranding, isLoading: isUploading } =
    usePostBrandingPackageLogo(handleRequest);

  // ---------------------------------------------------------------------------
  // State - Initial Values (from server)
  // ---------------------------------------------------------------------------
  const [initialLogoPreview, setInitialLogoPreview] = useState<ImagePreview>(DEFAULT_PREVIEW);
  const [initialFaviconPreview, setInitialFaviconPreview] = useState<ImagePreview>(DEFAULT_PREVIEW);
  const [initialTitlePrefix, setInitialTitlePrefix] = useState('');

  // ---------------------------------------------------------------------------
  // State - Pending Changes
  // ---------------------------------------------------------------------------
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoDelete, setPendingLogoDelete] = useState(false);
  const [pendingFaviconFile, setPendingFaviconFile] = useState<File | null>(null);
  const [pendingFaviconDelete, setPendingFaviconDelete] = useState(false);
  const [pendingTitlePrefix, setPendingTitlePrefix] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // State - Delete Modal
  // ---------------------------------------------------------------------------
  const [deleteType, setDeleteType] = useState<DeleteType | null>(null);
  const [formActions, setFormActions] = useState<FormActions | null>(null);
  const {
    value: isDeleteModalOpen,
    setFalse: closeDeleteModal,
    setTrue: openDeleteModal,
  } = useAdvanceBoolean<string>(false);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------
  const isSaving = isUploading;

  // User can make changes if they have either Create OR Edit permission
  const hasRequiredPermission = hasCreatePermission || hasEditPermission;

  const hasPendingChanges =
    pendingLogoFile !== null ||
    pendingLogoDelete ||
    pendingFaviconFile !== null ||
    pendingFaviconDelete ||
    pendingTitlePrefix !== null;

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // All branding data is inside the config JSON string
    const config = parseConfig(brandingPackage?.config);

    // Debug logging
    console.log('Branding config parsed:', config);
    console.log('Logo URL:', config.logo?.url);
    console.log('Favicon URL:', config['fav-icon']?.url);

    // Logo from config
    if (config.logo?.url) {
      const logoUrl = buildImageUrl(config.logo.url, config.logo.is_external);
      console.log('Built logo URL:', logoUrl);
      setInitialLogoPreview({
        content: logoUrl,
        name: 'Current Logo',
      });
    } else {
      setInitialLogoPreview(DEFAULT_PREVIEW);
    }

    // Favicon from config (kebab-case key)
    const favIcon = config['fav-icon'];
    if (favIcon?.url) {
      const faviconUrl = buildImageUrl(favIcon.url, favIcon.is_external);
      console.log('Built favicon URL:', faviconUrl);
      setInitialFaviconPreview({
        content: faviconUrl,
        name: 'Current Favicon',
      });
    } else {
      setInitialFaviconPreview(DEFAULT_PREVIEW);
    }

    // Title prefix from config (kebab-case key)
    const pageTitlePrefix = config['page-title-prefix'];
    setInitialTitlePrefix(pageTitlePrefix ?? '');
  }, [brandingPackage]);

  // ---------------------------------------------------------------------------
  // Handlers - Delete Modal
  // ---------------------------------------------------------------------------
  const handleOpenDeleteModal = useCallback(
    (type: DeleteType, actions: FormActions) => {
      setDeleteType(type);
      setFormActions(actions);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!formActions || !deleteType) return;

    // Reset form fields
    formActions.setFieldValue(deleteType, null);
    formActions.setFieldValue(`${deleteType}Preview`, DEFAULT_PREVIEW);
    formActions.setFieldTouched(deleteType, false);

    // Update pending state
    if (deleteType === 'logo') {
      setPendingLogoFile(null);
      setPendingLogoDelete(true);
    } else {
      setPendingFaviconFile(null);
      setPendingFaviconDelete(true);
    }

    closeDeleteModal();
  }, [formActions, deleteType, closeDeleteModal]);

  const getDeleteModalTitle = (): string => {
    const item = deleteType === 'logo' ? 'logo' : 'favicon';
    return `Are you sure you want to remove the ${item}? You will need to click Save to confirm this change.`;
  };

  // ---------------------------------------------------------------------------
  // Handlers - File Upload
  // ---------------------------------------------------------------------------
  const handleLogoChange = useCallback(
    async (
      file: File,
      setFieldValue: FormActions['setFieldValue'],
      setFieldTouched: FormActions['setFieldTouched'],
      validateField: (field: string) => void
    ) => {
      setFieldValue('logo', file);
      setFieldTouched('logo', true, false);
      validateField('logo');

      if (!isValidFile(file, 'logo')) {
        setPendingLogoFile(null);
        return;
      }

      setPendingLogoFile(file);
      setPendingLogoDelete(false);

      const dataUrl = await readFileAsDataUrl(file);
      setFieldValue('logoPreview', { content: dataUrl, name: file.name });
    },
    []
  );

  const handleFaviconChange = useCallback(
    async (
      file: File,
      setFieldValue: FormActions['setFieldValue'],
      setFieldTouched: FormActions['setFieldTouched'],
      validateField: (field: string) => void
    ) => {
      setFieldValue('favicon', file);
      setFieldTouched('favicon', true, false);
      validateField('favicon');

      if (!isValidFile(file, 'favicon')) {
        setPendingFaviconFile(null);
        return;
      }

      setPendingFaviconFile(file);
      setPendingFaviconDelete(false);

      const dataUrl = await readFileAsDataUrl(file);
      setFieldValue('faviconPreview', { content: dataUrl, name: file.name });
    },
    []
  );

  const handleTitlePrefixChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    ) => {
      handleChange(e);
      const newValue = e.target.value;
      setPendingTitlePrefix(newValue !== initialTitlePrefix ? newValue : null);
    },
    [initialTitlePrefix]
  );

  // ---------------------------------------------------------------------------
  // Handlers - Save
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(() => {
    // Build payload with all pending changes
    const payload: {
      File?: Blob;
      ImageLink?: string;
      RemoveOldLogo?: boolean;
      Favicon?: Blob;
      FaviconLink?: string;
      RemoveOldFavicon?: boolean;
      PageTitlePrefix?: string;
    } = {};

    // Logo changes
    if (pendingLogoFile) {
      payload.File = pendingLogoFile;
    } else if (pendingLogoDelete) {
      payload.RemoveOldLogo = true;
    }

    // Favicon changes
    if (pendingFaviconFile) {
      payload.Favicon = pendingFaviconFile;
    } else if (pendingFaviconDelete) {
      payload.RemoveOldFavicon = true;
    }

    // Title prefix - always send current value (use pending if changed, otherwise use initial)
    payload.PageTitlePrefix = pendingTitlePrefix !== null ? pendingTitlePrefix : initialTitlePrefix;

    uploadBranding(payload, {
      onSuccess: () => {
        // Reset logo state
        if (pendingLogoDelete) setInitialLogoPreview(DEFAULT_PREVIEW);
        setPendingLogoFile(null);
        setPendingLogoDelete(false);

        // Reset favicon state
        if (pendingFaviconDelete) setInitialFaviconPreview(DEFAULT_PREVIEW);
        setPendingFaviconFile(null);
        setPendingFaviconDelete(false);

        // Reset title prefix state
        if (pendingTitlePrefix !== null) setInitialTitlePrefix(pendingTitlePrefix);
        setPendingTitlePrefix(null);
      },
    });
  }, [
    pendingLogoFile,
    pendingLogoDelete,
    pendingFaviconFile,
    pendingFaviconDelete,
    pendingTitlePrefix,
    uploadBranding,
  ]);

  // ---------------------------------------------------------------------------
  // Form Initial Values
  // ---------------------------------------------------------------------------
  const formInitialValues: BrandingFormValues = {
    logo: null,
    logoPreview: initialLogoPreview,
    favicon: null,
    faviconPreview: initialFaviconPreview,
    titlePrefix: initialTitlePrefix,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <PageWrapper id='whiteLabelConfigPageContainer'>
      {/* Page Header */}
      <PageHeader id='whiteLabelConfigPageHeader'>
        <Heading
          variant='h3'
          title='Branding Package Configuration'
          id='whiteLabelConfigPageTitle'
        />
        <Heading
          variant='body-s'
          title='Customize your branding with logo, favicon, and other configurations'
          color={COLORS.Gray}
          id='whiteLabelConfigPageDescription'
        />
      </PageHeader>

      {/* Form */}
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={WHITE_LABEL_LOGO_VALIDATION}
        onSubmit={handleSubmit}>
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          validateField,
          handleChange,
        }) => (
          <Form id='whiteLabelConfigForm'>
            <WhiteLabelContainer id='whiteLabelConfigContent'>
              <WhiteLabelSection id='whiteLabelBrandingSection'>
                {/* Favicon Configuration */}
                <div className='config-item' id='whiteLabelFaviconConfig'>
                  <div className='config-item-header' id='whiteLabelFaviconHeader'>
                    <Heading
                      variant='h4'
                      title='Favicon Configuration'
                      id='whiteLabelFaviconTitle'
                    />
                    <Heading
                      variant='body-s'
                      title='Upload your favicon (browser tab icon)'
                      color={COLORS.Gray}
                      id='whiteLabelFaviconDescription'
                    />
                  </div>
                  <div className='config-item-content' id='whiteLabelFaviconContent'>
                    <FaviconUpload
                      faviconPreview={values.faviconPreview}
                      onFaviconChange={(file) =>
                        handleFaviconChange(file, setFieldValue, setFieldTouched, validateField)
                      }
                      onDeleteClick={() =>
                        handleOpenDeleteModal('favicon', { setFieldValue, setFieldTouched })
                      }
                      errors={errors}
                      touched={touched}
                      isLoading={isLoading}
                      disabled={!hasRequiredPermission}
                      id='whiteLabelFaviconUpload'
                    />
                    <div className='logo-guidelines' id='whiteLabelFaviconGuidelines'>
                      <Heading
                        variant='body-s'
                        title='Favicon Guidelines:'
                        fontWeight={600}
                        id='whiteLabelFaviconGuidelinesTitle'
                      />
                      <ul id='whiteLabelFaviconGuidelinesList'>
                        <li>
                          <Heading
                            variant='body-xs'
                            title='Recommended size: 32x32 or 16x16 pixels'
                          />
                        </li>
                        <li>
                          <Heading
                            variant='body-xs'
                            title='Supported formats: ICO, PNG, JPG, JPEG'
                          />
                        </li>
                        <li>
                          <Heading variant='body-xs' title='Maximum file size: 512KB' />
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Logo Configuration */}
                <div className='config-item' id='whiteLabelLogoConfig'>
                  <div className='config-item-header' id='whiteLabelLogoHeader'>
                    <Heading variant='h4' title='Logo Configuration' id='whiteLabelLogoTitle' />
                    <Heading
                      variant='body-s'
                      title='Upload your company logo for branding'
                      color={COLORS.Gray}
                      id='whiteLabelLogoDescription'
                    />
                  </div>
                  <div className='config-item-content' id='whiteLabelLogoContent'>
                    <LogoUpload
                      logoPreview={values.logoPreview}
                      onLogoChange={(file) =>
                        handleLogoChange(file, setFieldValue, setFieldTouched, validateField)
                      }
                      onDeleteClick={() =>
                        handleOpenDeleteModal('logo', { setFieldValue, setFieldTouched })
                      }
                      errors={errors}
                      touched={touched}
                      isLoading={isLoading}
                      disabled={!hasRequiredPermission}
                      id='whiteLabelLogoUpload'
                    />
                    <div className='logo-guidelines' id='whiteLabelLogoGuidelines'>
                      <Heading
                        variant='body-s'
                        title='Logo Guidelines:'
                        fontWeight={600}
                        id='whiteLabelGuidelinesTitle'
                      />
                      <ul id='whiteLabelGuidelinesList'>
                        <li>
                          <Heading variant='body-xs' title='Recommended size: 200x60 pixels' />
                        </li>
                        <li>
                          <Heading variant='body-xs' title='Supported formats: PNG, JPG, JPEG' />
                        </li>
                        <li>
                          <Heading variant='body-xs' title='Maximum file size: 2MB' />
                        </li>
                        <li>
                          <Heading
                            variant='body-xs'
                            title='Use transparent background for best results'
                          />
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Browser Title Configuration */}
                <div className='config-item' id='whiteLabelTitleConfig'>
                  <div className='config-item-header' id='whiteLabelTitleHeader'>
                    <Heading
                      variant='h4'
                      title='Browser Title Configuration'
                      id='whiteLabelTitleTitle'
                    />
                    <Heading
                      variant='body-s'
                      title='Set a prefix for browser tab titles'
                      color={COLORS.Gray}
                      id='whiteLabelTitleDescription'
                    />
                  </div>
                  <div className='config-item-content' id='whiteLabelTitleContent'>
                    <div className='form-field' id='whiteLabelTitleField'>
                      <InputText
                        label='Title Prefix'
                        name='titlePrefix'
                        placeholder='e.g., MyCompany'
                        errors={errors}
                        touched={touched}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleTitlePrefixChange(e, handleChange)
                        }
                        disabled={!hasRequiredPermission}
                        id='whiteLabelTitlePrefixInput'
                      />
                      <Heading
                        variant='body-xs'
                        title='This prefix will appear before page titles (e.g., "MyCompany | Dashboard")'
                        color={COLORS.Gray}
                        className='title-prefix-field'
                        id='whiteLabelTitlePrefixHint'
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button - Only show if user has edit permission */}
                {hasRequiredPermission && (
                  <div className='section-actions' id='whiteLabelActions'>
                    <Button
                      as='Edit'
                      id='whiteLabelSaveButton'
                      title='Save Changes'
                      type='submit'
                      variant='primary'
                      loading={isSaving}
                      disabled={!hasPendingChanges || isSaving}
                    />
                  </div>
                )}
              </WhiteLabelSection>
            </WhiteLabelContainer>
          </Form>
        )}
      </Formik>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <AlertPopup
          isShow={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          customTitle={getDeleteModalTitle()}
          handleSubmit={handleDeleteConfirm}
          loading={false}
          id='deleteConfirmationModal'
        />
      )}
    </PageWrapper>
  );
};

export default WhiteLabelConfiguration;
